import { useState } from "react"
import Papa from "papaparse"
import { Upload, Button, Space, Typography, message } from "@mimber/ui-web"
import { UploadOutlined, DownloadOutlined } from "@mimber/ui-web"
import { IMPORT_MAX_FILE_SIZE_BYTES } from "@mimber/schema"
import type { UploadStepProps } from "./UploadStep.types"

const { Text, Paragraph } = Typography

const CSV_PREVIEW_ROW_COUNT = 5

function downloadTemplate(headers: string[], filename: string): void {
  const csv = Papa.unparse([headers])
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

/**
 * First wizard step: pick a CSV file. Parses only a small preview client-side
 * (for column detection) — the full file is re-parsed and validated
 * server-side once uploaded.
 */
export const UploadStep: React.FunctionComponent<UploadStepProps> = ({
  templateHeaders,
  templateFilename,
  onParsed,
}) => {
  const [isParsing, setIsParsing] = useState(false)

  const beforeUpload = (file: File) => {
    const isValidType =
      file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv")
    if (!isValidType) {
      message.error("You can only upload a .csv file!")
      return Upload.LIST_IGNORE
    }

    const isValidSize = file.size <= IMPORT_MAX_FILE_SIZE_BYTES
    if (!isValidSize) {
      message.error("File must be smaller than 5MB!")
      return Upload.LIST_IGNORE
    }

    setIsParsing(true)
    Papa.parse<Record<string, string>>(file, {
      header: true,
      preview: CSV_PREVIEW_ROW_COUNT,
      skipEmptyLines: true,
      complete: (results) => {
        setIsParsing(false)
        const headers = results.meta.fields ?? []
        if (headers.length === 0) {
          message.error("Couldn't detect any columns in that file")
          return
        }
        onParsed({ file, headers, previewRows: results.data })
      },
      error: () => {
        setIsParsing(false)
        message.error("Couldn't read that file as CSV")
      },
    })

    return false // Prevent antd's own auto-upload
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Paragraph type="secondary">
        Upload a CSV file with one row per person. The next step lets you map
        each column to the right field.
      </Paragraph>

      <Upload.Dragger
        multiple={false}
        maxCount={1}
        accept=".csv,text/csv"
        beforeUpload={beforeUpload}
        showUploadList={false}
        disabled={isParsing}
      >
        <p className="ant-upload-drag-icon">
          <UploadOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag a CSV file to this area to upload
        </p>
        <p className="ant-upload-hint">Max file size 5MB</p>
      </Upload.Dragger>

      <Button
        icon={<DownloadOutlined />}
        onClick={() => downloadTemplate(templateHeaders, templateFilename)}
      >
        Download template
      </Button>
      <Text type="secondary">
        Not sure what columns to use? Download the template above.
      </Text>
    </Space>
  )
}
